import type { TFunction } from 'i18next'
import type { SaasApiError } from '../../../api'

const VOICE_BINDING_ERROR_CODES = new Set([
  'VOICE_ACCESS_DENIED',
  'VOICE_NOT_FOUND',
  'VOICE_INVALID',
  'VOICE_STALE',
])

const PROVIDER_RAW_MARKERS = [
  "don't have access to this voice",
  'do not have access to this voice',
  'no access to this voice',
  'voice not found',
  'invalid voice',
]

const TEMPORARY_ERROR_MARKERS = [
  'timeout',
  'timed out',
  'network',
  'temporarily unavailable',
  '429',
  '502',
  '503',
  '504',
  'rate limit',
]

export type PersistedGenerateAudioError = {
  canonicalCode: string | null
  voiceBindingFailure: boolean
  retryable: boolean
}

function looksLikeRawProviderEnglish(message: string): boolean {
  const lowered = message.toLowerCase()
  if (/[\u4e00-\u9fff]/.test(message)) return false
  return PROVIDER_RAW_MARKERS.some((marker) => lowered.includes(marker))
}

function inferCanonicalCode(args: { code?: string | null; message?: string | null }): string | null {
  const code = (args.code || '').trim()
  if (VOICE_BINDING_ERROR_CODES.has(code)) return code
  const message = (args.message || '').trim()
  if (!message) return null
  const lowered = message.toLowerCase()
  if (PROVIDER_RAW_MARKERS.some((marker) => lowered.includes(marker))) {
    if (lowered.includes('not found')) return 'VOICE_NOT_FOUND'
    if (lowered.includes('invalid voice')) return 'VOICE_INVALID'
    return 'VOICE_ACCESS_DENIED'
  }
  if (code === 'voice_capability_not_ready' && looksLikeRawProviderEnglish(message)) {
    return 'VOICE_ACCESS_DENIED'
  }
  return code || null
}

/** Classify persisted Canvas fields / historical generation errors at render time. */
export function classifyPersistedGenerateAudioError(args: {
  message?: string | null
  errorCode?: string | null
}): PersistedGenerateAudioError {
  const canonicalCode = inferCanonicalCode({ code: args.errorCode, message: args.message })
  const voiceBindingFailure = canonicalCode ? VOICE_BINDING_ERROR_CODES.has(canonicalCode) : false
  const message = (args.message || '').trim().toLowerCase()
  const retryable =
    !voiceBindingFailure &&
    Boolean(message || args.errorCode) &&
    (TEMPORARY_ERROR_MARKERS.some((marker) => message.includes(marker)) ||
      (!looksLikeRawProviderEnglish(args.message || '') && !VOICE_BINDING_ERROR_CODES.has(canonicalCode || '')))
  return { canonicalCode, voiceBindingFailure, retryable }
}

/** Tenant-safe Generate Audio error copy — never surface raw provider English. */
export function formatGenerateAudioError(
  err: Pick<SaasApiError, 'code' | 'message' | 'status'> | null | undefined,
  t: TFunction<'canvas'>,
): string {
  if (!err) return t('generateAudio.errors.generateFailed')

  const code = (err.code || '').trim()
  const message = (err.message || '').trim()

  if (VOICE_BINDING_ERROR_CODES.has(code)) {
    return t('generateAudio.errors.voiceTemplateUnavailable')
  }

  if (looksLikeRawProviderEnglish(message)) {
    return t('generateAudio.errors.voiceTemplateUnavailable')
  }

  if (code === 'voice_capability_not_ready' || err.status === 503) {
    if (!message || looksLikeRawProviderEnglish(message)) {
      if (looksLikeRawProviderEnglish(message)) {
        return t('generateAudio.errors.voiceTemplateUnavailable')
      }
      return t('generateAudio.errors.serviceUnavailable')
    }
    if (/[\u4e00-\u9fff]/.test(message)) return message
    return t('generateAudio.errors.serviceUnavailable')
  }

  if (code === 'member_credit_limit_exceeded') {
    return t('generation.errors.memberCreditLimitExceeded')
  }
  if (code === 'insufficient_credits' || err.status === 402) {
    return t('generateAudio.errors.pointsInsufficient')
  }

  if (/timeout/i.test(message) || err.status === 504) {
    return t('generateAudio.errors.timeout')
  }

  if (message && /[\u4e00-\u9fff]/.test(message)) {
    return message
  }

  return t('generateAudio.errors.generateFailed')
}

export function formatPersistedGenerateAudioError(
  args: { message?: string | null; errorCode?: string | null },
  t: TFunction<'canvas'>,
): string | null {
  const message = (args.message || '').trim()
  if (!message) return null
  return formatGenerateAudioError(
    {
      code: inferCanonicalCode(args) || undefined,
      message,
      status: undefined,
    },
    t,
  )
}

export function isVoiceBindingGenerateAudioError(
  err: Pick<SaasApiError, 'code' | 'message'> | null | undefined,
): boolean {
  if (!err) return false
  return classifyPersistedGenerateAudioError({
    message: err.message,
    errorCode: err.code,
  }).voiceBindingFailure
}

export function isGenerationErrorRetryable(args: {
  message?: string | null
  errorCode?: string | null
  apiError?: Pick<SaasApiError, 'code' | 'message' | 'status'> | null
}): boolean {
  if (
    isVoiceBindingGenerateAudioError(args.apiError) ||
    classifyPersistedGenerateAudioError({ message: args.message, errorCode: args.errorCode }).voiceBindingFailure
  ) {
    return false
  }
  const persisted = classifyPersistedGenerateAudioError({ message: args.message, errorCode: args.errorCode })
  if (args.apiError) {
    return classifyPersistedGenerateAudioError({
      message: args.apiError.message,
      errorCode: args.apiError.code,
    }).retryable
  }
  return persisted.retryable
}

export function resolveVoiceTemplateUnavailableState(args: {
  voiceBindingBlocked: boolean
  voiceTemplateExecutable: boolean | null
  selectedVoiceTemplateExecutable: boolean | null
  errorMessage?: string | null
  errorCode?: string | null
}): boolean {
  if (args.voiceBindingBlocked) return true
  if (args.voiceTemplateExecutable === false || args.selectedVoiceTemplateExecutable === false) return true
  return classifyPersistedGenerateAudioError({
    message: args.errorMessage,
    errorCode: args.errorCode,
  }).voiceBindingFailure
}
